import React, { useMemo } from "react";
import { WEBSITE_BUILDER_Z_LAYERS } from "../../../../utility/websiteBuilderZLayers";
import { IWebsiteBuilderSectionEntityViewBoundingBox } from "../../hooks/sectionEntity/helpers/sectionEntityViewBoundingBox";
import { isWebsiteBuilderSectionEntityInSectionContext, TWebsiteBuilderSectionEntityViewContext } from "../../hooks/sectionEntity/helpers/sectionEntityViewContext";
import { useWebsiteBuilderEditorContext } from "../../../../websiteBuilderEditorContext";
import { TSectionEntityFrameHighlight } from "./SectionEntityFrame";
import classNames from "classnames";

interface ISectionEntityFrameHighlightTitleProps {
  context: TWebsiteBuilderSectionEntityViewContext;
  title: string;
  highlight: TSectionEntityFrameHighlight;
  boundingBox: IWebsiteBuilderSectionEntityViewBoundingBox;
}

const TITLE_CONTAINER_HEIGHT = 20;
const TITLE_CONTAINER_OFFSET = 1;

export function SectionEntityFrameHighlightTitle(props: ISectionEntityFrameHighlightTitleProps) {
  const { context, title, highlight, boundingBox } = props;

  const { activePageDomNodeRefMap } = useWebsiteBuilderEditorContext();

  const showOnTop = useMemo(() => {
    if (highlight !== "none" ) {
      if (isWebsiteBuilderSectionEntityInSectionContext(context)) {
        return boundingBox.y > TITLE_CONTAINER_HEIGHT + TITLE_CONTAINER_OFFSET;
      } else if (
        context.type === "element-in-container" ||
        context.type === "element-in-grid-cell" ||
        context.type === "element-in-tab"
      ) {
        const containerId =
          context.type === "element-in-container" ? context.parentNode.id : (
            context.type === "element-in-grid-cell" ? context.gridNode.id : context.tabsetNode.id
          );

        const elementTop = activePageDomNodeRefMap.current.get(context.node.id)?.getBoundingClientRect()?.top ?? 0;
        const containerTop = activePageDomNodeRefMap.current.get(containerId)?.getBoundingClientRect()?.top ?? 0;
        const spaceOnTop = elementTop - containerTop > TITLE_CONTAINER_HEIGHT + TITLE_CONTAINER_OFFSET;

        return spaceOnTop;
      } else {
        return false;
      }
    } else {
      return false;
    }
  }, [highlight, boundingBox.y, context, context.type, context.node.id, context.parentNode.id]);

  return (
    <>
      {highlight !== "none" && (
        <div
          className={classNames("website-builder-section-entity-frame-highlight-title-container", {
            "frame-highlight-title-container-weak": highlight === "weak",
            "frame-highlight-title-container-strong": highlight === "strong",
          })}
          style={{
            left: boundingBox.x - TITLE_CONTAINER_OFFSET,
            top: showOnTop
              ? boundingBox.y - TITLE_CONTAINER_HEIGHT - TITLE_CONTAINER_OFFSET
              : boundingBox.y + boundingBox.height + TITLE_CONTAINER_OFFSET,
            height: TITLE_CONTAINER_HEIGHT,
            borderRadius: showOnTop ? "5px 5px 0 0" : "0 0 5px 5px",
            zIndex: WEBSITE_BUILDER_Z_LAYERS.designSpace.sectionEntityFrame,
          }}
        >
          <div className="frame-highlight-title-positioner">
            <div
              className={classNames("frame-highlight-title-positioner-title", {
                "frame-highlight-title-positioner-title-weak": highlight === "weak",
                "frame-highlight-title-positioner-title-strong": highlight === "strong",
              })}
            >
              {title}
            </div>
          </div>
        </div>
      )}
    </>
  );
}
